/**
 * Lavandería
 *
 * Vista principal de gestión de lavandería. El selector arriba lista
 * TODAS las lavanderías del catálogo (incluida la genérica externa,
 * marcada con tag "Sistema") y el filtro pasa el `id` al backend:
 * `WHERE lavanderia_id = id`.
 *
 * UX bifurcada según `lavanderia.es_generica` (round 2026-05-12 iter 28
 * + iter 34):
 *   - AF INTERNA (es_generica=false): 2 sub-tabs "Ingresos" y "Gastos y
 *     utilidad". La sub-entidad tiene contabilidad propia (servicios,
 *     sueldos, materiales) que se materializa en lavanderia_af_gastos.
 *     Utilidad = ingresos_total (intermediario+recibe) − gastos_op.
 *     Tabla: cantidades + precios + extras + descripción.
 *   - GENÉRICA EXTERNA (es_generica=true): 2 sub-tabs:
 *       · "Presupuesto estimado" (auto rol='recibe' del booking). Los
 *         estimados son AUTO (no se crean a mano; Alert info lo explica),
 *         pero el botón "Anotar costo real" (round 2026-07-21) sí está
 *         acá: salta al sub-tab "Costo real" y abre el modal, para que el
 *         admin no tenga que descubrir el otro sub-tab. Tabla con
 *         cantidades/precios.
 *       · "Costo real" (manual rol='paga') — botón "Nuevo costo real"
 *         abre modal en `costoRealMode`. La tabla muestra columnas
 *         financieras (Nombre, Rol AF, Pagado por, Pagado a) SIN
 *         cantidades/precios/extras.
 *     3 Statistic cards: Estimado / Real / Utilidad — se muestran UNA sola
 *     vez ARRIBA de los sub-tabs (round 2026-07-21; antes iban dentro de
 *     cada tab). Son agregados globales idénticos en ambos sub-tabs (el
 *     rolFilter NO los afecta, ver lavadosEmpresa). La genérica NO tiene
 *     servicios/sueldos/materiales — es solo un proveedor externo, no una
 *     sub-entidad operacional.
 *
 * Permite CRUD de lavados manuales y reasignación entre lavanderías
 * (UPDATE de `lavanderia_id`). Los lavados venidos de un booking
 * (booking_id != null) son solo-lectura — para corregir hay que crear
 * un registro paralelo manual.
 *
 * Histórico de rounds aplicados al archivo:
 *   - iter 17 (2026-05-10): unificación de tablas legacy `lavanderia` +
 *     `lavanderia_af` → `lavados`.
 *   - iter 18 fase B (2026-05-11): DROP TABLE de las legacy.
 *   - iter 24 (2026-05-11): la genérica externa pasa a vivir como fila
 *     normal del catálogo (es_generica=true). El caso lavanderia_id IS
 *     NULL queda solo para huérfanos legacy (data fantasma).
 *   - iter 26 (2026-05-12): modelo financiero unificado — toda lavandería
 *     es sub-entidad con contabilidad propia (intermediario AF interna,
 *     Estimado/Real genérica).
 *   - iter 28 (2026-05-12): UX bifurcada por es_generica + modal simple
 *     "Costo real" + columna `lavados.nombre` opcional.
 *   - iter 29 (2026-05-12): KPIs separados Estimado/Real para la genérica
 *     en /finanzas-empresa.
 *   - iter 31 (2026-05-12): documentación reconciliada.
 *   - iter 32 (2026-05-12): validaciones rol↔es_generica en backend; el
 *     cron ya no materializa filas en lavanderia_af_gastos para la
 *     genérica; cards /finanzas-empresa alineadas con /lavanderia.
 *
 * Toda la lógica de fetch + mutación vive en useLavanderiaLogic.
 */

import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Col,
  Select,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Input,
  Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
  ClearOutlined,
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  fetchAllPages,
  exportToXlsxSingleSheet,
} from "../finanzas-empresa/exportXls";
import { useSearchParams } from "react-router-dom";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { apiFetch } from "../../services/apiClient";
import { getMsg, getModal } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatEstadiaCode } from "../../utils/estadiaCode";
import dayjs from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import {
  useLavanderiaLogic,
  mapLavadoRow,
  LavadoRecord,
  ModoLavanderia,
} from "./useLavanderiaLogic";
import LavanderiasTab from "./LavanderiasTab";
import LavadoForm from "./components/LavadoForm";
import GastosOperacionalesTable from "./components/GastosOperacionalesTable";
import { AdjuntosLavanderia, useAdjuntosLavanderia } from "./components/AdjuntosLavanderia";
import "./lavanderia.css";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Formateador CLP ─────────────────────────────────────────────────────────

const clp = (v?: number | null) =>
  v != null
    ? new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
      }).format(v)
    : "—";

// ─── Columnas compartidas ─────────────────────────────────────────────────────

/**
 * Tipo de vista que decide qué columnas mostrar:
 *  - "af-interna": tabla completa con cantidades, precios, extras (lavados
 *    de servicios reales, tienen sentido).
 *  - "estimado": vista del sub-tab "Presupuesto estimado" de la genérica
 *    externa. Lavados auto del booking — mantenemos cantidades/precios
 *    porque vienen del propietario.lavanderia_total y son informativos.
 *  - "real": vista del sub-tab "Costo real" de la genérica externa.
 *    Lavados manuales anotados a mano. NO tienen cantidades/precios/extras
 *    (round 2026-05-12 iter 34) — el costo real es un movimiento financiero
 *    agregado, no un detalle de operación. En su lugar mostramos nombre +
 *    pagado por + pagado a (sentido financiero relevante).
 */
type VistaLavados = "af-interna" | "estimado" | "real";

// Round 2026-05-12 iter 34 + iter 36: helper para renderizar bonito un
// pago JSONB `{ tipo, nombre, usuario_id }`. Round iter 36: cuando el
// pago es `tipo=5` (lavandería del catálogo) y trae `usuario_id`,
// resolvemos el nombre REAL desde el catálogo de lavanderías cargado.
// Antes el helper confiaba en `pago.nombre` que podía venir genérico
// ("Lavandería AF" literal sin distinguir Papudo/Ñuñoa/etc.) en lavados
// pre-iter 36. Si la lavandería no está en el catálogo (FK rota o id
// huérfano), caemos a "Lavandería #<id>" para que el admin pueda al
// menos buscarla por id.
const renderPago = (
  pago: { tipo?: number; nombre?: string; usuario_id?: number | null } | null | undefined,
  lavanderiasCatalog: { id: number; nombre: string }[],
): React.ReactNode => {
  if (!pago) return "—";
  if (Number(pago.tipo) === 5 && pago.usuario_id != null) {
    const lav = lavanderiasCatalog.find((l) => l.id === pago.usuario_id);
    if (lav) return lav.nombre;
    return `Lavandería #${pago.usuario_id}`;
  }
  return pago.nombre || "—";
};

const buildColumns = (
  esGeneral: boolean,
  vista: VistaLavados,
  propiedades: { id: number; nombre: string }[],
  // Round 2026-05-12 iter 36: catálogo de lavanderías para resolver el
  // nombre real en las columnas Pagado por / Pagado a cuando tipo=5.
  lavanderiasCatalog: { id: number; nombre: string }[],
  onEditar: (r: LavadoRecord) => void,
  // Round 2026-05-12 iter 37: las acciones que mutan ahora reciben el
  // flag `fromBooking` para que el wrapper decida si mostrar el
  // Modal.confirm de advertencia antes de tocar el backend.
  onEliminar: (id: number, opts: { fromBooking: boolean }) => void,
  onMover: (id: number, opts: { fromBooking: boolean }) => void,
  // Round 2026-05-12 iter 36: botón "Recalcular" para registros de
  // booking que quedaron inconsistentes (rol/lavandería desfasados de la
  // cascada actual). Dispara POST /api/stays/recalc-booking/:bookingId
  // y la cascada regenera el lavado con los valores canónicos.
  onRecalcular: (record: LavadoRecord) => void,
): ColumnsType<LavadoRecord> => {
  const colFecha: ColumnsType<LavadoRecord>[number] = {
    title: "Fecha",
    dataIndex: "fecha",
    key: "fecha",
    width: 110,
    render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
    sorter: (a, b) => (a.fecha ?? "").localeCompare(b.fecha ?? ""),
  };

  const colPropiedad: ColumnsType<LavadoRecord>[number] = {
    title: "Propiedad",
    dataIndex: "propiedadId",
    key: "propiedadId",
    width: 160,
    render: (id) =>
      id
        ? formatPropertyName(propiedades.find((p) => p.id === id)?.nombre) ||
          `#${id}`
        : "—",
  };

  // Columnas que solo aplican a registros con cantidades/precios reales
  // (lavados operativos AF interna o estimados auto del booking).
  // Round iter 34: NO se muestran en vista "real" — los costos reales
  // anotados a mano son agregados, no tienen este desglose.
  const colsDetalleLavado: ColumnsType<LavadoRecord> = [
    {
      title: "Lavados",
      dataIndex: "cantidadLavados",
      key: "cantidadLavados",
      width: 80,
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Secados",
      dataIndex: "cantidadSecados",
      key: "cantidadSecados",
      width: 80,
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "P. Lavado",
      dataIndex: "precioLavado",
      key: "precioLavado",
      width: 110,
      align: "right",
      render: clp,
    },
    {
      title: "P. Secado",
      dataIndex: "precioSecado",
      key: "precioSecado",
      width: 110,
      align: "right",
      render: clp,
    },
  ];

  const colMonto: ColumnsType<LavadoRecord>[number] = {
    title: "Monto",
    dataIndex: "monto",
    key: "monto",
    width: 120,
    align: "right",
    render: (v) => <Text strong>{clp(v)}</Text>,
    sorter: (a, b) => (a.monto ?? 0) - (b.monto ?? 0),
  };

  // Columnas que solo aplican al sub-tab "Costo real" (round iter 34):
  // nombre/concepto y la estructura financiera relevante (rol AF + pagados).
  // Para estimado/AF interna las puntas viven dentro del pago y no aportan
  // tanto valor en la tabla — se ven en el modal Ver/Editar.
  const colNombre: ColumnsType<LavadoRecord>[number] = {
    title: "Nombre / concepto",
    dataIndex: "nombre",
    key: "nombre",
    width: 200,
    ellipsis: true,
    render: (v) => v ?? "—",
  };

  const colPagadoPor: ColumnsType<LavadoRecord>[number] = {
    title: "Pagado por",
    dataIndex: "pagadoPor",
    key: "pagadoPor",
    width: 160,
    ellipsis: true,
    render: (pago) => renderPago(pago, lavanderiasCatalog),
  };

  const colPagadoA: ColumnsType<LavadoRecord>[number] = {
    title: "Pagado a",
    dataIndex: "pagadoA",
    key: "pagadoA",
    width: 160,
    ellipsis: true,
    render: (pago) => renderPago(pago, lavanderiasCatalog),
  };

  const colsExtras: ColumnsType<LavadoRecord> = [
    {
      title: "Extra monto",
      dataIndex: "extraMonto",
      key: "extraMonto",
      width: 120,
      align: "right",
      render: clp,
    },
    {
      // Round 2026-05-07: width fijo para que el header no se trunque a
      // "E. D." cuando todas las celdas son "—". El ellipsis sigue
      // truncando contenido largo dentro de la celda con tooltip nativo.
      title: "Extra detalle",
      dataIndex: "extraDetalle",
      key: "extraDetalle",
      width: 140,
      ellipsis: true,
      render: (v) => v ?? "—",
    },
  ];

  const colDescripcion: ColumnsType<LavadoRecord>[number] = {
    title: "Descripción",
    dataIndex: "descripcion",
    key: "descripcion",
    width: 160,
    ellipsis: true,
    render: (v) => v ?? "—",
  };

  const colRolAF: ColumnsType<LavadoRecord>[number] = {
    title: "Rol AF",
    dataIndex: "rolDeAlojaFacil",
    key: "rolDeAlojaFacil",
    width: 130,
    render: (v) => (v ? <Tag color="blue">{v}</Tag> : "—"),
  };

  const colOrigen: ColumnsType<LavadoRecord>[number] = {
    // Round 2026-05-07: identifica si el registro vino de un arriendo
    // (webhook de Stays / cascada de booking) o si fue creado a mano. Solo
    // los manuales son editables/eliminables desde acá — los de arriendo
    // se corrigen creando otro registro paralelo (lavado o gasto operacional).
    title: "Origen",
    key: "origen",
    width: 180,
    render: (_, record) => {
      if (record.bookingId) {
        // Código mostrado: `id_reservac` (formato JS11J) cuando la reserva vino
        // de Stays/Airbnb — el código corto que ya usan en la plataforma origen,
        // reconocible al instante en /calendario y al buscar en Stays. Si no hay
        // (reserva manual), se cae al «Código de estadía AF» (booking.id →
        // «Arriendo #N»), que es el identificador universal. El numero_corto
        // (AF-…) quedó legacy — round 2026-07-20.
        const codigoCorto = record.bookingIdReservac;
        const label = codigoCorto
          ? `Arriendo ${codigoCorto}`
          : `Arriendo #${record.bookingId}`;

        // Round 2026-05-08 (iter 12) — tooltip rico, mismo formato que el
        // del calendario (cal-tooltip-content / cal-tooltip-row), para que
        // el admin vea los detalles de la reserva sin tener que ir a otra
        // pantalla. Si los campos del booking llegan null (ej: sync viejo
        // sin todos los campos), se omiten esas filas.
        const fmtClp = (v: number | null | undefined) =>
          `$${(Number(v) || 0).toLocaleString("es-CL")}`;
        const fmtFecha = (s: string | null | undefined) =>
          s ? s.split("-").reverse().join("-") : "—";
        const fmtCreada = (s: string | null | undefined) => {
          if (!s) return null;
          try {
            return new Date(s).toLocaleString("es-CL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          } catch {
            return null;
          }
        };
        const creadaTxt = fmtCreada(record.bookingFechaCreacion);
        // «Ref. plataforma» = id_reservac (JS11J), solo si la reserva vino de
        // una plataforma. El identificador universal es «Estadía #N» (abajo).
        // El numero_corto (AF-…) quedó legacy — round 2026-07-20.
        const idShown = record.bookingIdReservac ?? null;

        // Estilos inline (en vez de reusar las classes `cal-tooltip-*` del
        // calendario) porque calendario.css solo se carga cuando se visita
        // /calendario. Mantenemos el formato visual idéntico (título +
        // filas con flex space-between + nota al pie con border-top sutil).
        const rowStyle: React.CSSProperties = {
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          fontSize: 12,
          lineHeight: 1.4,
        };
        const labelStyle: React.CSSProperties = { opacity: 0.75 };

        const tooltipContent = (
          <div style={{ minWidth: 220, padding: "2px 0" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                marginBottom: 6,
                paddingBottom: 4,
                borderBottom: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {record.bookingNombreHuesped ?? "—"}
            </div>
            {record.bookingReservaTotalClp != null && (
              <div style={rowStyle}>
                <span style={labelStyle}>Total reserva</span>
                <strong>{fmtClp(record.bookingReservaTotalClp)}</strong>
              </div>
            )}
            {record.bookingPrecioNochesClp != null && (
              <div style={rowStyle}>
                <span style={labelStyle}>Precio/noche</span>
                <strong>{fmtClp(record.bookingPrecioNochesClp)}</strong>
              </div>
            )}
            {record.bookingIngreso && record.bookingSalida && (
              <div style={rowStyle}>
                <span style={labelStyle}>Fechas</span>
                <span>
                  {fmtFecha(record.bookingIngreso)} →{" "}
                  {fmtFecha(record.bookingSalida)}
                </span>
              </div>
            )}
            {idShown && (
              <div style={rowStyle}>
                <span style={labelStyle}>Ref. plataforma</span>
                <strong>{idShown}</strong>
              </div>
            )}
            {record.bookingId != null && (
              <div style={rowStyle}>
                <span style={labelStyle}>Estadía</span>
                <strong>{formatEstadiaCode(record.bookingId)}</strong>
              </div>
            )}
            {creadaTxt && (
              <div style={rowStyle}>
                <span style={labelStyle}>Creada</span>
                <span>{creadaTxt}</span>
              </div>
            )}
            {record.bookingIdReserval && !record.bookingIdReservac && (
              <div style={rowStyle}>
                <span style={labelStyle}>ID externo</span>
                <span style={{ fontSize: 10, opacity: 0.7 }}>
                  {record.bookingIdReserval}
                </span>
              </div>
            )}
            <div
              style={{
                marginTop: 8,
                paddingTop: 6,
                borderTop: "1px solid rgba(255,255,255,0.18)",
                fontSize: 11,
                opacity: 0.85,
                lineHeight: 1.35,
              }}
            >
              Generado desde la reserva. Para corregir el monto crea un
              registro paralelo (lavado o gasto operacional).
            </div>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="left"
            mouseEnterDelay={0.3}
          >
            <Tag color="purple" style={{ whiteSpace: "nowrap", cursor: "help" }}>
              {label}
            </Tag>
          </Tooltip>
        );
      }
      return (
        <Tooltip title="Registro creado manualmente. Editable y eliminable.">
          <Tag color="default">Manual</Tag>
        </Tooltip>
      );
    },
  };

  const colAcciones: ColumnsType<LavadoRecord>[number] = {
    title: "Acciones",
    key: "acciones",
    fixed: "right",
    width: 190,
    align: "center",
    render: (_, record) => {
      // Round 2026-06-19 (cascada bidireccional D-B): los lavados de booking
      // son editables/eliminables/movibles con un Modal.confirm que explica la
      // CASCADA. El monto del lavado es el ESTIMADO de lavandería cobrado al
      // huésped; editar/eliminar PROPAGA al arriendo (recalcula su costo de
      // limpieza + la liquidación del cliente + el desglose del aseo). Mover
      // solo cambia la lavandería destino (no afecta la liquidación). El botón
      // "Recalcular" sigue disponible para regenerar todo desde el arriendo.
      const fromBooking = record.bookingId != null;
      return (
        <Space size={4}>
          {fromBooking ? (
            <Tooltip title="Editar el monto (se propaga al arriendo y recalcula la liquidación)">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditar(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Editar">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEditar(record)}
              />
            </Tooltip>
          )}

          {/* Round 2026-05-12 iter 36: botón "Recalcular" solo para lavados
              de booking. Es el CAMINO LIMPIO de resincronización: regenera
              el registro desde la cascada del arriendo (POST
              /api/stays/recalc-booking/:bookingId). Recomendado antes que
              editar manualmente. */}
          {fromBooking && (
            <Tooltip title="Recalcular desde el arriendo (resincroniza el registro con los valores actuales del booking — recomendado)">
              <Popconfirm
                title="¿Recalcular este lavado desde su arriendo?"
                description="La cascada regenerará el registro con los valores actuales. Sobrescribe cualquier corrección manual previa."
                onConfirm={() => onRecalcular(record)}
                okText="Sí, recalcular"
                cancelText="Cancelar"
              >
                <Button size="small" icon={<ReloadOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}

          <Tooltip
            title={
              fromBooking
                ? "Mover a otra lavandería (cambia el destino; no afecta la liquidación)"
                : esGeneral
                  ? "Mover a una Lavandería AF interna"
                  : "Mover a la Lavandería General Externa"
            }
          >
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={() => onMover(record.id, { fromBooking })}
            />
          </Tooltip>

          <Tooltip
            title={
              fromBooking
                ? "Eliminar (deja el arriendo sin cobro de lavandería y recalcula)"
                : "Eliminar"
            }
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onEliminar(record.id, { fromBooking })}
            />
          </Tooltip>
        </Space>
      );
    },
  };

  // Round 2026-05-12 iter 34: ensamblamos el array final según la vista.
  //   - "real" (sub-tab Costo real de la genérica): sin detalle de
  //     lavados/secados/precios/extras. En su lugar mostramos
  //     nombre/concepto + estructura financiera (rol AF + pagado por/a).
  //     Los costos reales son movimientos agregados anotados a mano, no
  //     operaciones detalladas.
  //   - "estimado" / "af-interna": tabla completa con cantidades, precios
  //     y extras (vienen del propietario.lavanderia_total o del operativo).
  if (vista === "real") {
    return [
      colFecha,
      colPropiedad,
      colNombre,
      colDescripcion,
      colMonto,
      colRolAF,
      colPagadoPor,
      colPagadoA,
      colOrigen,
      colAcciones,
    ];
  }

  return [
    colFecha,
    colPropiedad,
    ...colsDetalleLavado,
    colMonto,
    ...colsExtras,
    colDescripcion,
    colRolAF,
    colOrigen,
    colAcciones,
  ];
};

// ─── Formulario de creación / edición ────────────────────────────────────────

// PrecioIvaInput y LavadoForm extraídos a components/LavadoForm.tsx
// (round 2026-04-27, Fase 8). Ver doc-comment en el componente.

// ─── Componente principal ─────────────────────────────────────────────────────

const LavanderiaPage: React.FC = () => {
  const {
    sidebarOpen,
    toggleSidebar,
    hayEnlaceDirecto,
    modo,
    lavanderias,
    handleModoChange,
    handleModoChangeSinRecordar,
    refreshLavanderias,
    data,
    total,
    page,
    pageSize,
    setPageSize,
    loading,
    fetchData,
    buildBody,
    dateRange,
    setDateRange,
    dateMode,
    setDateMode,
    dateMonth,
    setDateMonth,
    setDateModeSinRecordar,
    setDateMonthSinRecordar,
    setDateRangeSinRecordar,
    filtroPropiedad,
    setFiltroPropiedad,
    montoMin,
    setMontoMin,
    montoMax,
    setMontoMax,
    extraSearch,
    setExtraSearch,
    handleLimpiarFiltros,
    // Round 2026-05-12 iter 34 (antes iter 28): rolFilter + costoRealMode
    // del modal + agregados Estimado/Real.
    rolFilter,
    setRolFilter,
    setRolFilterSinRecordar,
    costoRealMode,
    totalIngresoEstimado,
    totalCostoReal,
    totalIntermediado,
    handleNuevoCostoReal,
    handleNuevoIntermediado,
    modalOpen,
    editingRecord,
    saving,
    form,
    handleNuevo,
    handleEditar,
    handleGuardar,
    handleCancelar,
    handleEliminar,
    handleMover,
    moverModalOpen,
    moverDestinoId,
    setMoverDestinoId,
    handleConfirmarMover,
    handleCancelarMover,
    propiedades,
    usuarios,
    usuariosPorRol,
    crearOtro,
    ROLES,
  } = useLavanderiaLogic();
  const adjuntosLavados = useAdjuntosLavanderia(typeof modo === "number" ? modo : null, { lavados: data.map((r) => r.id) });

  // Las dos pestañas se recuerdan (round 2026-09-24). Antes las manejaba AntD
  // sola (`defaultActiveKey`) y siempre abrían en "Registros" / "Ingresos".
  // Con un drill-down desde /finanzas-empresa no se restauran: ese enlace
  // apunta a la tabla de registros y abrir otra pestaña la escondería.
  const [tabPrincipal, setTabPrincipal] = useFiltroPersistido<
    "registros" | "lavanderias"
  >("lavanderia.pestana", "registros", {
    validar: (v) => v === "registros" || v === "lavanderias",
    restaurar: !hayEnlaceDirecto,
  });
  // Sub-pestaña de una lavandería AF interna. Solo se ve cuando la lavandería
  // elegida no es la genérica externa.
  const [tabAfInterna, setTabAfInterna] = useFiltroPersistido<
    "ingresos" | "gastos"
  >("lavanderia.pestanaAf", "ingresos", {
    validar: (v) => v === "ingresos" || v === "gastos",
    restaurar: !hayEnlaceDirecto,
  });

  // `esGeneral` = el modo seleccionado apunta a la lavandería genérica
  // externa (es_generica=true en el catálogo). Solo se usa para invertir
  // el copy del botón "Mover a Lavandería AF/externa" en cada fila. La
  // tabla de gastos operacionales se renderiza igual para genérica y AF
  // internas desde iter 23 commit A (2026-05-11).
  const lavSeleccionada = lavanderias.find((l) =>
    typeof modo === "number" ? l.id === modo : false,
  );
  const esGeneral = lavSeleccionada?.es_generica === true;

  // ── Deep-link desde /finanzas-empresa → Lavandería (round 2026-07-15) ──────
  // La pestaña Lavandería de /finanzas-empresa enlaza acá con
  // `?lavanderiaId=<id>&mes=YYYY-MM` (o `&desde&hasta` para un rango) para que
  // el admin haga drill-down a la tabla granular filtrada 1:1 con las cifras
  // del resumen (mismo patrón que el "Ver detalle" de la pestaña Aseos → /aseos).
  // Se aplica UNA sola vez al montar y luego se limpian los params consumidos
  // de la URL, para no re-aplicar en refresh ni pisar cambios manuales del
  // usuario. `handleModoChangeSinRecordar` fija la lavandería; `refreshLavanderias`
  // (que corre en paralelo) respeta ese modo si el id existe en el catálogo.
  // Todo lo que aplica este efecto lo trae la URL, no lo eligió el admin: va con
  // los setters que NO guardan, o el drill-down le dejaría esa lavandería y ese
  // período como sus filtros preferidos. Ver docs/filtros-persistentes.md §5.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const lavParam = searchParams.get("lavanderiaId");
    const mesParam = searchParams.get("mes");
    const desdeParam = searchParams.get("desde");
    const hastaParam = searchParams.get("hasta");
    if (!lavParam && !mesParam && !desdeParam && !hastaParam) return;
    deepLinkAppliedRef.current = true;

    if (lavParam) {
      const id = Number(lavParam);
      if (Number.isFinite(id)) handleModoChangeSinRecordar(id);
    }
    if (desdeParam && hastaParam) {
      const d = dayjs(desdeParam);
      const h = dayjs(hastaParam);
      if (d.isValid() && h.isValid()) {
        setDateModeSinRecordar("rango");
        setDateRangeSinRecordar([d.startOf("day"), h.endOf("day")]);
      }
    } else if (mesParam) {
      const m = dayjs(`${mesParam}-01`);
      if (m.isValid()) {
        setDateModeSinRecordar("mes");
        setDateMonthSinRecordar(m.startOf("month"));
      }
    }

    // Limpia solo los params consumidos (deja intactos otros si los hubiera).
    const next = new URLSearchParams(searchParams);
    ["lavanderiaId", "mes", "desde", "hasta"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Round 2026-05-07: cuando el lavado viene de un arriendo, el botón
  // Round 2026-05-12 iter 37: el modo "Ver" (readOnly) se removió. Los
  // lavados de booking ahora se editan con el mismo modal de creación —
  // con la advertencia de desincronización mostrada al abrirlo y el
  // flag confirmDesync seteado al guardar. Si el admin solo quiere
  // mirar, abre y cancela.
  const viewMode = false;
  const handleCancelarLocal = () => {
    handleCancelar();
  };

  // ─── Export XLS (Ingresos) ─────────────────────────────────────────────
  // Round 2026-05-08 (iter 6): exporta TODOS los lavados que matchean los
  // filtros actuales (lavandería, fechas, propiedad, monto, extras), no
  // solo la página visible. Reusa `buildBody` del hook + `fetchAllPages`
  // del helper compartido — mismo patrón que finanzas-empresa.
  const [exportingIngresos, setExportingIngresos] = useState(false);
  const handleExportIngresos = async () => {
    setExportingIngresos(true);
    const hide = getMsg()?.loading("Preparando exportación...", 0);
    try {
      const allRaw = await fetchAllPages<any>(async (p, sz) => {
        const body = buildBody(p, sz);
        if (typeof modo === "number") {
          const existingFields = body.filters?.fields ?? {};
          body.filters = {
            ...body.filters,
            fields: { ...existingFields, lavanderia_id: { eq: modo } },
          };
        }
        return await finanzasEmpresaApi.searchLavados(body);
      });

      // Normalizamos con el mismo mapper que usa la tabla — el backend
      // mezcla camelCase y snake_case según el endpoint, así que sin
      // mapLavadoRow las columnas Propiedad / Rol AF / Origen quedaban
      // vacías o mal mapeadas en el XLS aunque la tabla las mostrara
      // correctamente.
      const all = allRaw.map(mapLavadoRow);

      const propMap = new Map(propiedades.map((p) => [p.id, p.nombre]));
      const rows = all.map((r) => ({
        Fecha: r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "",
        Propiedad: r.propiedadId
          ? (propMap.get(r.propiedadId) ?? `#${r.propiedadId}`)
          : "",
        Lavados: r.cantidadLavados ?? 0,
        Secados: r.cantidadSecados ?? 0,
        "P. Lavado": Number(r.precioLavado) || 0,
        "P. Secado": Number(r.precioSecado) || 0,
        Monto: Number(r.monto) || 0,
        "Extra monto": Number(r.extraMonto) || 0,
        "Extra detalle": r.extraDetalle ?? "",
        Descripción: r.descripcion ?? "",
        "Rol AF": r.rolDeAlojaFacil ?? "",
        Origen: r.bookingId
          ? `Arriendo ${r.bookingIdReservac || `#${r.bookingId}`}`
          : "Manual",
      }));

      await exportToXlsxSingleSheet({
        filename: `lavanderia-ingresos-${modoLabel.replace(/\s+/g, "-").toLowerCase()}-${ahoraChile().format("YYYYMMDD-HHmm")}`,
        sheetName: "Ingresos",
        rows,
      });
      getMsg()?.success(`Exportadas ${rows.length} filas`);
    } catch (err: any) {
      getMsg()?.error(err?.message || "Error al exportar");
    } finally {
      hide?.();
      setExportingIngresos(false);
    }
  };

  // Round 2026-05-12 iter 34: la vista decide qué columnas mostrar.
  //   - Genérica + sub-tab Costo real (rolFilter='paga') → vista "real"
  //     (sin lavados/secados/precios/extras; con nombre + pagado_por/a).
  //   - Genérica + sub-tab Intermediado (rolFilter='intermediario', round
  //     2026-07-21) → vista "real" también (mismas columnas: nombre +
  //     pagado_por = a quién se le cobra + pagado_a = proveedor externo).
  //   - Genérica + sub-tab Presupuesto estimado (rolFilter='recibe')
  //     → vista "estimado" (cantidades/precios completos, vienen del booking).
  //   - AF interna (rolFilter=null) → vista "af-interna" (igual a estimado
  //     visualmente: cantidades + extras del operativo real).
  const vista: VistaLavados = esGeneral
    ? rolFilter === "paga" || rolFilter === "intermediario"
      ? "real"
      : "estimado"
    : "af-interna";

  // Round 2026-05-12 iter 36: handler del botón "Recalcular" en la
  // columna Acciones. Solo se invoca con records que tienen booking_id
  // != null (la columna lo enforce). Dispara el endpoint canónico de
  // recálculo que ya usa el botón homónimo en /calendario.
  const handleRecalcular = async (record: LavadoRecord) => {
    if (!record.bookingId) return;
    const res = await apiFetch<any>(
      `/stays/recalc-booking/${record.bookingId}`,
      {
        method: "POST",
        successMsg: "Lavado recalculado desde el arriendo",
        errorMsg: "Error al recalcular",
      },
    );
    if (res != null) fetchData(page);
  };

  // Round 2026-06-19 (cascada bidireccional D-B): wrappers que muestran un
  // Modal.confirm explicando la CASCADA antes de mutar un lavado de arriendo.
  // El monto del lavado es el ESTIMADO de lavandería cobrado al huésped;
  // editarlo/eliminarlo PROPAGA al arriendo y recalcula (costo de limpieza,
  // liquidación del cliente, desglose del aseo). Ya NO es una desincronización.
  // (El body sigue enviando `confirmDesync: true` por compat; el backend ahora
  // lo ignora y propaga igual.)

  // Aviso de EDICIÓN VINCULADA (cascada bidireccional D-B, round 2026-06-19).
  // El monto de un lavado de arriendo es el ESTIMADO de lavandería cobrado al
  // huésped (parte del costo de limpieza del arriendo). Editar/eliminar ese
  // monto ahora PROPAGA al arriendo y recalcula solo (subítem lavandería, costo
  // de limpieza, liquidación del cliente y el desglose del aseo) — ya NO es una
  // "desincronización". Mover solo cambia la lavandería destino (no afecta el
  // monto ni la liquidación). Si el arriendo es de un mes cerrado, no se aplica.
  const avisoLavadoVinculado = (operacion: "editar" | "eliminar" | "mover") => {
    if (operacion === "mover") {
      return (
        <div style={{ lineHeight: 1.5 }}>
          <p style={{ marginTop: 0 }}>
            <strong>Este lavado está vinculado a un arriendo.</strong> Mover solo
            cambia a qué lavandería se asigna (AF interna ↔ General Externa).
          </p>
          <p style={{ marginBottom: 0 }}>
            El monto cobrado al huésped y la <strong>liquidación del cliente no
            cambian</strong> — solo cambia a qué sub-entidad de lavandería se
            imputa el flujo en los reportes.
          </p>
        </div>
      );
    }
    return (
      <div style={{ lineHeight: 1.5 }}>
        <p style={{ marginTop: 0 }}>
          <strong>Este lavado está vinculado a un arriendo.</strong> Su monto es
          el valor de lavandería que se le cobra al huésped (parte del costo de
          limpieza del arriendo).
        </p>
        <p>
          Al {operacion === "editar" ? "editar su monto" : "eliminarlo"}, el
          cambio <strong>se propaga al arriendo</strong> y se recalcula solo:
        </p>
        <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
          <li>
            el subítem de lavandería y el <strong>costo de limpieza</strong> del
            arriendo;
          </li>
          <li>
            la <strong>liquidación del cliente</strong> (la limpieza se descuenta
            de su pago);
          </li>
          <li>el desglose del <strong>aseo</strong> vinculado.</li>
        </ul>
        <p style={{ marginBottom: 0 }}>
          {operacion === "eliminar"
            ? "Eliminar deja el arriendo sin cobro de lavandería ($0)."
            : "La comisión de Aloja Fácil no cambia (la lavandería es un pass-through al huésped)."}{" "}
          Si el arriendo es de un mes ya cerrado, el cambio no se aplica.
        </p>
      </div>
    );
  };

  const wrapEditar = (record: LavadoRecord) => {
    if (record.bookingId == null) {
      handleEditar(record);
      return;
    }
    getModal()?.confirm({
      title: "¿Editar el monto de un lavado de arriendo?",
      icon: <ExclamationCircleOutlined style={{ color: "#1677ff" }} />,
      content: avisoLavadoVinculado("editar"),
      okText: "Sí, editar (recalcula el arriendo)",
      cancelText: "Cancelar",
      width: 560,
      onOk: () => {
        handleEditar(record);
      },
    });
  };

  const wrapEliminar = (id: number, opts: { fromBooking: boolean }) => {
    if (!opts.fromBooking) {
      // Path corto: el viejo Popconfirm se removió de la tabla. Ahora
      // todos los eliminar pasan por Modal.confirm (manual o booking).
      getModal()?.confirm({
        title: "¿Eliminar este lavado manual?",
        icon: <ExclamationCircleOutlined style={{ color: "#cf1322" }} />,
        content: "Esta acción no se puede deshacer.",
        okText: "Sí, eliminar",
        okButtonProps: { danger: true },
        cancelText: "Cancelar",
        onOk: () => handleEliminar(id),
      });
      return;
    }
    getModal()?.confirm({
      title: "¿Eliminar un lavado generado desde un arriendo?",
      icon: <ExclamationCircleOutlined style={{ color: "#cf1322" }} />,
      content: avisoLavadoVinculado("eliminar"),
      okText: "Sí, eliminar (deja el arriendo sin lavandería)",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      width: 560,
      onOk: () => handleEliminar(id, { confirmDesync: true }),
    });
  };

  const wrapMover = (id: number, opts: { fromBooking: boolean }) => {
    if (!opts.fromBooking) {
      getModal()?.confirm({
        title: esGeneral
          ? "¿Mover a una Lavandería AF interna?"
          : "¿Mover a la Lavandería General Externa?",
        icon: <ExclamationCircleOutlined style={{ color: "#1677ff" }} />,
        content:
          "El registro va a pasar al catálogo destino y dejará de aparecer en esta vista.",
        okText: "Sí, mover",
        cancelText: "Cancelar",
        onOk: () => handleMover(id),
      });
      return;
    }
    getModal()?.confirm({
      title: "¿Mover un lavado generado desde un arriendo?",
      icon: <ExclamationCircleOutlined style={{ color: "#1677ff" }} />,
      content: avisoLavadoVinculado("mover"),
      okText: "Sí, mover",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      width: 560,
      onOk: () => handleMover(id, { confirmDesync: true }),
    });
  };

  const columns = buildColumns(
    esGeneral,
    vista,
    propiedades,
    lavanderias,
    wrapEditar,
    wrapEliminar,
    wrapMover,
    handleRecalcular,
  );
  columns.splice(columns.length - 1, 0, {
    title: "Adjuntos", key: "adjuntos", width: 95,
    render: (_value, record) => <AdjuntosLavanderia grupos={adjuntosLavados.grupos.filter((g) => g.movimiento.id === record.id)} actualizar={adjuntosLavados.cargar} />,
  });

  // Round 2026-05-12 iter 28: sincroniza rolFilter al cambiar de
  // lavandería. Cuando se entra a la genérica externa, defaulteamos al
  // sub-tab "Estimado" (rolFilter='recibe' — round 2026-07-21, antes era el
  // alias 'recibe_intermediario'). Cuando se sale a una AF interna, limpiamos
  // el filtro (la tabla muestra todo).
  //
  // Round 2026-09-24: sin catálogo cargado, `esGeneral` todavía no significa
  // nada — es false porque `lavanderias` está vacío, no porque la lavandería
  // elegida sea AF interna. Sincronizar en ese primer render borraba el
  // sub-tab recordado ('paga' → null → 'recibe') y disparaba dos consultas de
  // más. Y se escribe con `setRolFilterSinRecordar` porque esto lo deduce el
  // sistema: guardarlo pisaba la elección del admin (entrar por el enlace de
  // /finanzas-empresa le dejaba 'recibe' como preferencia).
  React.useEffect(() => {
    if (lavanderias.length === 0) return;
    if (esGeneral && rolFilter === null) {
      setRolFilterSinRecordar("recibe");
    } else if (!esGeneral && rolFilter !== null) {
      setRolFilterSinRecordar(null);
    }
  }, [lavanderias.length, esGeneral, rolFilter, setRolFilterSinRecordar]);

  // Opciones del selector: TODAS las lavanderías del catálogo usan su
  // id numérico real (incluida la genérica). El filtro pasa el id al
  // backend y se ejecuta `WHERE lavanderia_id = id`.
  const modoOptions = lavanderias.map((l) => ({
    value: l.id as ModoLavanderia,
    label: l.es_generica ? `${l.nombre}  ·  Sistema` : l.nombre,
  }));

  const modoLabel = lavSeleccionada?.nombre ?? "Lavandería";

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="lav-container"
      contentClass="lav-content"
    >
      <div className="lav-page">
        <PageHeader
          title="Lavandería"
          helpText="Gestión de lavados por lavandería. Filtra por período y tipo de lavandería."
        />

        <Tabs
          activeKey={tabPrincipal}
          onChange={(k) => setTabPrincipal(k as "registros" | "lavanderias")}
          items={[
            {
              key: "registros",
              label: "Registros",
              children: (
                <>
        {/* Header */}
        <div className="lav-header">

          {/*
            Selector de lavandería — solo filtro de tabla. El destino
            del save lo decide el form (campo "Pagado a/por (rol) =
            Lavandería AF" + la lavandería elegida); el backend
            persiste el `lavanderia_id` del body y aplica la regla
            financiera por catálogo. Ver docs/modulo-lavanderia.md §4.

            El selector de fechas (Mes/Rango) vive en el toolbar del
            sub-tab "Ingresos" porque solo aplica allí — la tabla de
            "Gastos y utilidad" es mensual por diseño.
          */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>
              Filtrar tabla por lavandería:
            </span>
            <Select<ModoLavanderia>
              value={modo}
              onChange={handleModoChange}
              options={modoOptions}
              style={{ minWidth: 280 }}
            />
          </div>
        </div>

        {/* Banner educacional según el tipo de lavandería seleccionada
            (round 2026-05-11 iter 26). Explica el modelo financiero
            distinto entre genérica externa y AF interna. */}
        {esGeneral ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Lavandería General Externa — Estimado vs Real"
            description={
              <>
                Esta lavandería representa proveedores externos como sub-entidad.
                Tiene dos sub-tabs:
                <ul style={{ marginTop: 6, marginBottom: 6, paddingLeft: 22 }}>
                  <li>
                    <strong>Presupuesto estimado</strong>: lavados auto-generados
                    desde reservas (Origen = <em>Arriendo XXX</em>). Es lo que
                    AF cobró al huésped por el subitem lavandería. No se crean
                    a mano — se generan al crear/modificar el arriendo.
                  </li>
                  <li>
                    <strong>Costo real</strong>: anota lo que realmente pagas a
                    cada proveedor externo concreto. El modal pide nombre +
                    descripción + monto + estructura financiera (rol AF +
                    pagador). El destino del pago siempre es esta lavandería.
                  </li>
                </ul>
                Las cards arriba muestran <strong>Estimado</strong>,{" "}
                <strong>Real</strong> y <strong>Utilidad</strong> (Estimado −
                Real). Para AF Empresa, solo el costo real impacta la utilidad
                — el ingreso del subitem ya está contabilizado en arriendos.
                <br />
                <strong>Regla del modal manual (iter 38):</strong> el destinatario
                del pago es <strong>SIEMPRE</strong> esta lavandería. Solo puedes
                variar quién paga (AF, propietario, arrendatario, etc.) y el rol
                AF (paga / intermediario).
              </>
            }
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Lavandería AF interna — sub-entidad con utilidad propia"
            description={
              <>
                Esta es una lavandería de Aloja Fácil con contabilidad propia.
                Recibe automáticamente el subitem lavandería de cada booking
                (rol intermediario, AF transfiere el monto exacto cobrado al
                huésped). Sus gastos operacionales (servicios/sueldos/materiales)
                son de la sub-entidad y no afectan la utilidad de AF Empresa.
                <br />
                <strong>Regla del modal manual (iter 38):</strong> al crear un
                lavado a mano desde el botón "Nuevo registro", esta lavandería
                es <strong>SIEMPRE</strong> la receptora del pago. Solo puedes
                variar quién paga (AF típico vía intermediario, propietario,
                arrendatario, etc.). Los lavados AUTO de arriendo no pasan por
                este modal — los genera la cascada.
              </>
            }
          />
        )}

        {/* Barra de filtros */}
        <div className="lav-filters">
          {/* Filtro de PERÍODO (Mes / Rango). Round 2026-07-15: antes vivía
              enterrado dentro del sub-tab, debajo de las cards de KPIs — el
              usuario no lo encontraba y creía que /lavanderia "no filtra por
              fecha". Lo subimos acá, a la barra principal, como primer filtro
              (siempre visible). Refleja además el mes del deep-link entrante
              desde /finanzas-empresa (?mes=YYYY-MM), así el admin ve y puede
              cambiar el período pre-filtrado. Aplica a las dos vistas
              (Estimado/Real de la genérica e Ingresos de las AF internas). */}
          <Space size={4} wrap>
            <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>
              Período:
            </span>
            <Radio.Group
              size="small"
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: "Mes", value: "mes" },
                { label: "Rango", value: "rango" },
              ]}
            />
            {dateMode === "mes" ? (
              <DatePicker
                picker="month"
                value={dateMonth}
                onChange={(v) => setDateMonth(v)}
                format="MMMM YYYY"
                allowClear
                placeholder="Seleccionar mes"
                style={{ minWidth: 180 }}
              />
            ) : (
              <RangePicker
                value={dateRange}
                onChange={(v) => setDateRange(v as [any, any] | null)}
                format="DD/MM/YYYY"
                allowClear
                placeholder={["Desde", "Hasta"]}
              />
            )}
          </Space>

          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Filtrar por propiedad"
            value={filtroPropiedad}
            onChange={setFiltroPropiedad}
            options={propiedades.map((p) => ({
              value: p.id,
              label: formatPropertyName(p.nombre),
            }))}
            style={{ minWidth: 220, flex: 2 }}
            maxTagCount="responsive"
          />

          <InputNumber
            placeholder="Monto mín"
            value={montoMin}
            onChange={(v) => setMontoMin(v)}
            min={0}
            prefix="$"
            style={{ width: 140 }}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
            parser={(v) => Number((v ?? "").replace(/\./g, ""))}
          />

          <InputNumber
            placeholder="Monto máx"
            value={montoMax}
            onChange={(v) => setMontoMax(v)}
            min={0}
            prefix="$"
            style={{ width: 140 }}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
            parser={(v) => Number((v ?? "").replace(/\./g, ""))}
          />

          <Input
            placeholder="Buscar en extras..."
            value={extraSearch}
            onChange={(e) => setExtraSearch(e.target.value)}
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
          />

          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={() => fetchData(1)}
          >
            Buscar
          </Button>

          <Tooltip title="Limpiar filtros">
            <Button icon={<ClearOutlined />} onClick={handleLimpiarFiltros} />
          </Tooltip>
        </div>

        {/*
          Round 2026-05-08 (segunda iteración): los filtros de arriba son
          comunes a las dos vistas que da el tab Registros — Ingresos
          (lavados) y Gastos y utilidad (gastos operacionales). Antes
          ambas vivían apiladas; ahora separadas en sub-tabs para que el
          admin enfoque visualmente uno u otro y no haga scroll vertical
          interminable. Los filtros (lavandería, fechas, propiedad,
          monto, extras) actúan como header compartido.

          El sub-tab "Gastos y utilidad" siempre se renderiza para que
          el admin SIEMPRE vea la opción y entienda la estructura. Si la
          lavandería seleccionada es la genérica externa, en vez de la
          tabla mostramos un mensaje explicativo (round 2026-05-08 iter 3
          — antes el sub-tab se ocultaba con la genérica y el admin no
          sabía si faltaba o si no aplicaba).
        */}
        {/*
          Round 2026-05-12 iter 28: la genérica externa tiene UI distinta
          según pedido del owner.
            - Genérica externa: 2 sub-tabs ("Presupuesto estimado" + "Costo
              real") + Statistic cards (Estimado / Real / Utilidad). El
              cambio de sub-tab cambia el rolFilter del fetch:
              recibe_intermediario (estimado, autos del booking) vs paga
              (costo real, manuales).
            - AF interna: sub-tabs originales ("Ingresos" + "Gastos y
              utilidad"). La tabla de gastos op. mensual sigue siendo
              relevante (servicios/sueldos/materiales).
          La declaración de las variables JSX (toolbar, tabla, statistics)
          arriba evita duplicación entre las 2 ramas.
        */}
        {(() => {
          // El filtro de período (Mes/Rango) ahora vive en la barra de filtros
          // principal (arriba), visible siempre. Round 2026-07-15 — antes se
          // declaraba acá (`dateToolbar`) y se renderizaba dentro de este
          // toolbar, debajo de las cards; el usuario no lo veía.
          const tablaIngresos = (
            <Table<LavadoRecord>
              rowKey="id"
              size="small"
              loading={loading}
              dataSource={data}
              columns={columns}
              scroll={{ x: 1400 }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (t) => `${t} registros`,
                onChange: (p) => fetchData(p),
                onShowSizeChange: (_, newSize) => {
                  setPageSize(newSize);
                  fetchData(1, newSize);
                },
              }}
            />
          );

          const statisticEstimadoReal = (
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Presupuesto estimado (ingresos estimados)"
                    value={totalIngresoEstimado}
                    formatter={(v) => clp(Number(v))}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Costo real (ingresos reales)"
                    value={totalCostoReal}
                    formatter={(v) => clp(Number(v))}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Utilidad (Estimado − Real)"
                    value={totalIngresoEstimado - totalCostoReal}
                    formatter={(v) => clp(Number(v))}
                    valueStyle={{
                      color:
                        totalIngresoEstimado - totalCostoReal >= 0
                          ? "#3f8600"
                          : "#cf1322",
                    }}
                  />
                </Card>
              </Col>
              {/* Round 2026-07-21: card NEUTRA del pass-through 'intermediario'.
                  Separada a propósito de Estimado/Real/Utilidad: NO es ni
                  estimado ni costo real, y NO afecta la utilidad de AF (AF le
                  cobró a alguien y le pagó lo mismo al proveedor → neto 0). Se
                  muestra solo para transparencia del volumen. */}
              <Col xs={24} sm={12} lg={6}>
                <Tooltip title="AF le cobró este monto a alguien (cliente/arrendatario) y le pagó lo mismo al proveedor externo. Neto AF = 0: no suma ni resta a la utilidad; queda acá solo para ver el volumen.">
                  <Card size="small">
                    <Statistic
                      title="Intermediado (neto 0)"
                      value={totalIntermediado}
                      formatter={(v) => clp(Number(v))}
                      valueStyle={{ color: "#8c8c8c" }}
                    />
                  </Card>
                </Tooltip>
              </Col>
            </Row>
          );

          const buildIngresosToolbar = (
            onNuevo: (() => void) | null,
            btnLabel: string,
          ) => (
            <div
              className="lav-toolbar"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 12,
              }}
            >
              {/* Round 2026-05-12 iter 34: el botón "Nuevo registro" se
                  oculta cuando el sub-tab activo NO admite creación manual
                  (caso: "Presupuesto estimado" de la genérica externa —
                  los registros vienen SOLO de la cascada del booking). */}
              {onNuevo && (
                <Button type="primary" icon={<PlusOutlined />} onClick={onNuevo}>
                  {btnLabel}
                </Button>
              )}
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportIngresos}
                loading={exportingIngresos}
              >
                Exportar a XLS
              </Button>
            </div>
          );

          if (esGeneral) {
            // Vista de la Lavandería General Externa: 3 sub-tabs claros
            // (round 2026-07-21):
            //   1. "Presupuesto estimado" (auto del booking, rol='recibe').
            //   2. "Costo real" (anotado a mano, rol='paga').
            //   3. "Intermediado (neto 0)" (rol='intermediario'): pass-through
            //      donde AF le cobra a alguien y le paga lo mismo al proveedor
            //      externo. NO es ni estimado ni costo real, y NO afecta la
            //      utilidad de AF Empresa — por eso vive en su propio sub-tab,
            //      separado, para no ensuciar el "Estimado − Real".
            // La utilidad se muestra siempre arriba como Statistic — no hay
            // sub-tab de "gastos op." porque la genérica no tiene desglose
            // servicios/sueldos/materiales.
            const activeKey =
              rolFilter === "paga"
                ? "real"
                : rolFilter === "intermediario"
                  ? "intermediado"
                  : "estimado";
            // Round 2026-07-21: anotar un costo real desde CUALQUIER sub-tab.
            // Salta primero al sub-tab "Costo real" para que el registro recién
            // creado aparezca en la lista (la tabla del sub-tab "estimado" solo
            // muestra rol IN ('recibe','intermediario')).
            const anotarCostoReal = () => {
              setRolFilter("paga");
              handleNuevoCostoReal();
            };
            return (
              <>
                {/* Round 2026-07-21: las 3 cards (resumen global
                    Estimado/Real/Utilidad) se muestran UNA sola vez, ARRIBA de
                    los sub-tabs. Son idénticas en ambos sub-tabs (agregados
                    globales, ver finanzasEmpresa.controller.js → lavadosEmpresa)
                    — antes se renderizaban DENTRO de cada tab y parecían parte
                    del sub-tab, lo que confundía al admin. */}
                {statisticEstimadoReal}
                <Tabs
                  activeKey={activeKey}
                  onChange={(key) =>
                    setRolFilter(
                      key === "real"
                        ? "paga"
                        : key === "intermediado"
                          ? "intermediario"
                          : "recibe",
                    )
                  }
                  items={[
                    {
                      key: "estimado",
                      label: "Presupuesto estimado",
                      children: (
                        <div className="lav-section">
                          {/* Los lavados estimados son AUTO (cascada del
                              booking); no se crean a mano. El botón de abajo
                              anota un COSTO REAL y salta al sub-tab homónimo. */}
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Estos registros se generan automáticamente"
                            description={
                              <>
                                Cada arriendo nuevo crea (o actualiza) un lavado
                                estimado con el monto del subitem lavandería que
                                AF cobró al huésped. No se crean a mano. Para
                                anotar lo que el proveedor externo realmente
                                cobró, usa <strong>Anotar costo real</strong>. Si
                                AF solo hizo de intermediario (le cobró a alguien
                                y le pagó lo mismo al proveedor, sin ganar ni
                                perder), regístralo en{" "}
                                <strong>Intermediado (neto 0)</strong>.
                              </>
                            }
                          />
                          {/* Round 2026-07-21: el botón para anotar el costo
                              real ahora también vive acá (antes solo en el
                              sub-tab "Costo real", y el admin no lo encontraba).
                              `anotarCostoReal` salta al sub-tab "Costo real" y
                              abre el modal. */}
                          {buildIngresosToolbar(
                            anotarCostoReal,
                            "Anotar costo real",
                          )}
                          {tablaIngresos}
                        </div>
                      ),
                    },
                    {
                      key: "real",
                      label: "Costo real",
                      children: (
                        <div className="lav-section">
                          {buildIngresosToolbar(
                            handleNuevoCostoReal,
                            "Nuevo costo real",
                          )}
                          {tablaIngresos}
                        </div>
                      ),
                    },
                    {
                      // Round 2026-07-21: sub-tab del pass-through neto 0.
                      key: "intermediado",
                      label: "Intermediado (neto 0)",
                      children: (
                        <div className="lav-section">
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="Intermediación: Aloja Fácil no gana ni pierde"
                            description={
                              <>
                                Usa esto cuando AF hace de intermediario: le{" "}
                                <strong>cobra</strong> el lavado a alguien
                                (cliente/arrendatario) y le <strong>paga</strong>{" "}
                                el mismo monto al proveedor externo. El neto para
                                AF Empresa es <strong>cero</strong>: no suma ni
                                resta a la utilidad (ni al presupuesto estimado ni
                                al costo real). Igual queda registrado el cobro a
                                quien corresponda y el pago a la lavandería
                                externa.
                              </>
                            }
                          />
                          {buildIngresosToolbar(
                            handleNuevoIntermediado,
                            "Nuevo intermediado",
                          )}
                          {tablaIngresos}
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            );
          }

          // Vista de una Lavandería AF interna: estructura original
          // (Ingresos + Gastos y utilidad con desglose de servicios).
          return (
            <Tabs
              activeKey={tabAfInterna}
              onChange={(k) => setTabAfInterna(k as "ingresos" | "gastos")}
              items={[
                {
                  key: "ingresos",
                  label: "Ingresos",
                  children: (
                    <div className="lav-section">
                      {buildIngresosToolbar(handleNuevo, "Nuevo registro")}
                      {tablaIngresos}
                    </div>
                  ),
                },
                {
                  key: "gastos",
                  label: "Gastos y utilidad",
                  children:
                    typeof modo === "number" ? (
                      <GastosOperacionalesTable
                        lavanderiaId={modo}
                        lavanderiaName={lavSeleccionada?.nombre ?? "AF"}
                      />
                    ) : (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message="Selecciona una lavandería"
                        description="Elige una lavandería del filtro de arriba para ver y editar sus gastos operacionales."
                      />
                    ),
                },
              ]}
            />
          );
        })()}
                </>
              ),
            },
            {
              key: "lavanderias",
              label: "Gestión de lavanderías",
              children: <LavanderiasTab onCatalogChanged={refreshLavanderias} />,
            },
          ]}
        />
      </div>

      {/* Modal CRUD — en viewMode oculta el botón OK (footer={[Cerrar]}) */}
      <Modal
        open={modalOpen}
        title={
          viewMode
            ? `Ver lavado — ${modoLabel}`
            : editingRecord
              ? `Editar lavado — ${modoLabel}`
              : `Nuevo lavado — ${modoLabel}`
        }
        onOk={handleGuardar}
        onCancel={handleCancelarLocal}
        okText={editingRecord ? "Guardar cambios" : "Crear"}
        cancelText={viewMode ? "Cerrar" : "Cancelar"}
        confirmLoading={saving}
        // En viewMode reemplazamos el footer entero por un solo botón
        // "Cerrar" — el OK no tiene sentido si el form es solo lectura.
        footer={
          viewMode
            ? [
                <Button key="close" onClick={handleCancelarLocal}>
                  Cerrar
                </Button>,
              ]
            : undefined
        }
        width={700}
        forceRender
      >
        <LavadoForm
          form={form}
          propiedades={propiedades}
          usuarios={usuarios}
          usuariosPorRol={usuariosPorRol}
          crearOtro={crearOtro}
          ROLES={ROLES}
          lavanderias={lavanderias}
          modo={modo}
          readOnly={viewMode}
          costoRealMode={costoRealMode}
          esVinculado={editingRecord?.bookingId != null}
        />
      </Modal>

      {/* Modal elegir lavandería destino */}
      <Modal
        open={moverModalOpen}
        title="¿A qué lavandería mover?"
        onOk={handleConfirmarMover}
        onCancel={handleCancelarMover}
        okText="Mover"
        okButtonProps={{ disabled: !moverDestinoId }}
        cancelText="Cancelar"
        width={400}
      >
        <Select
          placeholder="Seleccionar lavandería destino"
          value={moverDestinoId}
          onChange={setMoverDestinoId}
          options={lavanderias
            .filter((l) => l.activo)
            .map((l) => ({ value: l.id, label: l.nombre }))}
          style={{ width: "100%" }}
          size="large"
        />
      </Modal>

    </Layout>
  );
};

export default LavanderiaPage;
